import { Component, Input, OnInit, OnChanges, AfterViewInit, inject, TemplateRef, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatTableModule, MatTableDataSource } from '@angular/material/table';
import { MatPaginatorModule, MatPaginator } from '@angular/material/paginator';
import { MatDialogModule, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { MatInputModule } from '@angular/material/input';
import { forkJoin, of, Subject } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';

import { DashboardService } from '../board.service';
import { AuthService } from '../../../services/auth.service';
import { BoardConfigService } from '../board-config.service';
import {
  CodeBoardConfig, TabConfig, DashCardConfig,
  QueryDef, QueryParam, BoardFilter,
} from '../board-config.model';

import { EchartCardComponent, EChartCardConfig } from '../echart-card/echart-card.component';
import { ChartjsCardComponent, ChartJsCardConfig } from '../chartjs-card/chartjs-card.component';
import { DarkKpiCardComponent } from '../dark-kpi-card/dark-kpi-card.component';
import { KpiStatusCardComponent, KpiStatusCardConfig } from '../kpi-status-card/kpi-status-card.component';
import {
  DarkTrendCardComponent,
  DarkRankingCardComponent,
  DarkProgressRingCardComponent,
  DarkTimelineCardComponent,
  DarkAchievementCardComponent, DarkAchievementConfig, AchievementBadge,
  LogoBannerComponent,
  DetectedObjectsCardComponent, DetectedObjectsConfig, DetectedObjectItem,
  BboxGalleryCardComponent, RecognitionVisualizationConfig, RecognitionRow, RecognitionObject,
  DetectionHeatmapCardComponent,
  RecognitionKpiCardComponent,
  ConfidenceTableCardComponent,
  PlanogramCardComponent,
  AreaCoverageCardComponent,
  StoreHeatmapCardComponent, StoreHeatmapCardConfig, StoreDetailRecord, CatalogEntry,
  FridgeAnalysisCardComponent, FridgeAnalysisCardConfig, FridgeSpacePanel, CANONICAL_SIZES,
  ContaminationCardComponent, ContaminationCardConfig,
} from '../custom-cards';
import { ThemeSwitcherComponent } from '../themes/theme-switcher/theme-switcher.component';
import { ThemeService } from '../themes/theme.service';
import { PageVisualizerClientComponent } from '../../recognition-visualizer/page-visualizer-client/page-visualizer-client.component';

// ─────────────────────────────────────────────────────────────────────────────

@Component({
  selector: 'app-code-dashboard',
  standalone: true,
  imports: [
    CommonModule, ReactiveFormsModule,
    MatFormFieldModule, MatSelectModule,
    MatDatepickerModule, MatNativeDateModule, MatInputModule,
    MatTableModule, MatPaginatorModule,
    MatDialogModule, MatIconModule, MatButtonModule, MatTooltipModule,
    EchartCardComponent, ChartjsCardComponent,
    DarkKpiCardComponent, KpiStatusCardComponent,
    DarkTrendCardComponent, DarkRankingCardComponent,
    DarkProgressRingCardComponent, DarkTimelineCardComponent,
    DarkAchievementCardComponent, LogoBannerComponent,
    DetectedObjectsCardComponent,
    BboxGalleryCardComponent,
    DetectionHeatmapCardComponent,
    RecognitionKpiCardComponent,
    ConfidenceTableCardComponent,
    PlanogramCardComponent,
    AreaCoverageCardComponent,
    StoreHeatmapCardComponent,
    FridgeAnalysisCardComponent,
    ContaminationCardComponent,
    ThemeSwitcherComponent, PageVisualizerClientComponent,
  ],
  templateUrl: './code-dashboard.component.html',
  styleUrl:    './code-dashboard.component.scss',
})
export class CodeDashboardComponent implements OnInit, OnChanges, AfterViewInit {

  @Input() boardId = '';
  @ViewChild('eventDialogTemplate') eventDialogTemplate!: TemplateRef<any>;
  @ViewChild(MatPaginator) paginator!: MatPaginator;

  private readonly svc        = inject(DashboardService);
  private readonly auth       = inject(AuthService);
  private readonly configSvc  = inject(BoardConfigService);
  private readonly dialog     = inject(MatDialog);
  private readonly themeSvc   = inject(ThemeService);

  currentDialogRef: MatDialogRef<any> | null = null;

  // ── Config cargada ──────────────────────────────────────────────────────
  boardConfig: CodeBoardConfig | null = null;

  // ── Estado global ───────────────────────────────────────────────────────
  errorTop    = false;
  configError = false;
  /** Filtros requeridos cuyo valor no se encontró en los parámetros del usuario */
  missingParams: string[] = [];

  // ── Valores de filtros activos (genérico) ───────────────────────────────
  /** Mapa key → valor actual de cada filtro */
  filterValues: Record<string, string> = {};
  /** Opciones de selects from_card: key del filtro → array de opciones */
  selectOptions: Record<string, string[]> = {};
  /** Raw data por filtro from_card (para filtrado local) */
  private rawFilterData: Record<string, Record<string, any>[]> = {};

  detailDateStart: Date = new Date();                                                  // hoy
  detailDateEnd:   Date = (() => { const d = new Date(); d.setDate(d.getDate() + 1); return d; })();  // hoy + 1 día

  // FormGroup reactivo requerido por mat-date-range-input para reflejar el valor correctamente
  detailRangeForm = new FormGroup({
    start: new FormControl<Date | null>(this.detailDateStart),
    end:   new FormControl<Date | null>(this.detailDateEnd),
  });

  // ── Tabs ────────────────────────────────────────────────────────────────
  dashTab = 'resumen';
  analysisSubTab: 'nevera' | 'contaminacion' | 'galeria' | 'heatmap' | 'objetos' | 'planograma' = 'nevera';
  private loadedTabs = new Set<string>();

  // ── Estados de carga por slot ───────────────────────────────────────────
  loadingSlots: Record<string, boolean> = {};

  // ── Banner ──────────────────────────────────────────────────────────────
  loadingBanner       = true;
  logoUrl             = '';
  logoTitle           = '';
  logoSubtitle        = '';
  logoBadge           = '';
  logoCompliancePercent: number | null = null;
  logoStatusLabel     = '';
  logoStatusColor     = '#22c55e';
  logoTrend: number[] = [];

  // ── Outputs de renders ──────────────────────────────────────────────────
  kpiStatusCard:   KpiStatusCardConfig | null = null;
  trendChartCard:  ChartJsCardConfig   | null = null;
  missionChartCard: ChartJsCardConfig  | null = null;
  achievementCard:  DarkAchievementConfig | null = null;
  rdvChartCard:    ChartJsCardConfig   | null = null;
  rankingChartCard: ChartJsCardConfig  | null = null;

  // ── Objetos detectados ───────────────────────────────────────────────────
  detectedObjectsCard:  DetectedObjectsConfig         | null = null;
  bboxGalleryCard:       RecognitionVisualizationConfig | null = null;
  detectionHeatmapCard:  RecognitionVisualizationConfig | null = null;
  recognitionKpiCard:    RecognitionVisualizationConfig | null = null;
  confidenceTableCard:   RecognitionVisualizationConfig | null = null;
  planogramCard:         RecognitionVisualizationConfig | null = null;
  areaCoverageCard:      RecognitionVisualizationConfig | null = null;
  storeHeatmapCard:      StoreHeatmapCardConfig    | null = null;
  storeHeatmapCardAlt:   StoreHeatmapCardConfig    | null = null;
  /** Loading flag mientras carga query 431 con detalles del PDV seleccionado */
  storeHeatmapDetailsLoading = false;
  /** Subject que serializa los clicks rápidos en PDV — switchMap cancela peticiones previas */
  private readonly storeSelected$ = new Subject<{ pdvName: string; pdvCode: string }>();
  fridgeAnalysisCard:    FridgeAnalysisCardConfig  | null = null;
  contaminationCard:     ContaminationCardConfig   | null = null;

  // ── Ejecuciones ─────────────────────────────────────────────────────────
  detailSdvRows: Record<string, any>[] = [];
  detailSdvDisplayKeys: string[] = [];
  detailDataSource = new MatTableDataSource<any>([]);
  loadedSdvDetail  = false;
  errorSdvDetail   = false;

  // ── Cache raw para filtros locales ──────────────────────────────────────
  private trendData:  Array<{ dia: string; aprobados: number }> = [];

  // ─────────────────────────────────────────────────────────────────────────
  ngOnInit(): void {
    this.bootstrap();
    this.themeSvc.setTheme('slate');
    this.setupStoreSelectedPipeline();
    this.detailRangeForm.valueChanges.subscribe(({ start, end }) => {
      if (!start || !end) return;
      this.detailDateStart = start;
      this.detailDateEnd   = end;
      this.onDetailDateChange();
    });
  }

  /**
   * Pipeline reactivo de selección de PDV:
   * - switchMap cancela la petición previa si el usuario hace clic en otro PDV
   * - cache simple: si ya tenemos los detalles de ese PDV en memoria, los reusamos
   */
  private setupStoreSelectedPipeline(): void {
    this.storeSelected$.pipe(
      switchMap(({ pdvName, pdvCode }) => {
        // Cache hit: detailsByStore ya tiene este PDV con data → no llamamos al backend
        const cached = this.storeHeatmapCard?.detailsByStore?.[pdvName];
        if (cached && cached.length > 0) {
          console.log('[431] cache hit para PDV:', pdvName, '→', cached.length, 'recs');
          return of({ pdvName, rows: null });    // null = ya está cargado
        }

        // Cache miss: llamar al query 431 con filtro_pdv_name (detalle por PDV con bboxes)
        this.storeHeatmapDetailsLoading = true;
        const params = [{
          id:     'filtro_pdv_name',
          type:   'string/=',
          value:  [pdvName],
          target: ['variable', ['template-tag', 'filtro_pdv_name']],
        }];
        return this.svc.getMetabaseCardJson(431, params).pipe(
          catchError(err => {
            console.error('[431] error cargando PDV:', pdvName, err);
            return of([] as Record<string, any>[]);
          }),
          switchMap((rows: any) => of({ pdvName, rows: rows as Record<string, any>[] })),
        );
      }),
    ).subscribe(({ pdvName, rows }) => {
      this.storeHeatmapDetailsLoading = false;
      if (rows === null) return;

      console.log('[431] pdv_name:', JSON.stringify(pdvName), '→', rows.length, 'recs');

      // Reutilizamos buildStoreDetailsFrom432 porque el schema de objetos_detectados es idéntico.
      // Pasamos el catálogo (query 436) para resolver nombre+atributos cuando
      // query 431 ya no los embebe directamente en cada objeto detectado.
      const catalog = this.storeHeatmapCard?.catalog;
      const builtByStore = this.buildStoreDetailsFrom432(rows, catalog);

      // Diagnóstico: qué keys produjo buildStoreDetailsFrom432
      const builtKeys = Object.keys(builtByStore);
      console.log('[431] builtByStore keys:', builtKeys, '| catálogo:', catalog?.size ?? 0, 'entradas');
      if (rows[0]) {
        const sampleKeys = Object.keys(rows[0]);
        console.log('[431] primera fila — keys disponibles:', sampleKeys);
        console.log('[431] primera fila — objetos_detectados (tipo):', typeof rows[0]['objetos_detectados'],
                    '(valor preview):', JSON.stringify(rows[0]['objetos_detectados'])?.slice(0, 200));
      }

      const records =
        builtByStore[pdvName] ??
        builtByStore['_all_'] ??
        Object.values(builtByStore)[0] ??
        [];
      console.log('[431] records asignados a pdvName:', pdvName, '→', records.length,
                  '| primer record objectName:', records[0]?.objectName);

      // Actualizar el config inmutablemente para que el OnPush detecte el cambio
      this.storeHeatmapCard = this.storeHeatmapCard
        ? {
            ...this.storeHeatmapCard,
            detailsByStore: {
              ...(this.storeHeatmapCard.detailsByStore ?? {}),
              [pdvName]: records,
            },
          }
        : this.storeHeatmapCard;
    });
  }

  /** Llamado desde el template cuando el usuario hace clic en un marcador del mapa */
  onStoreSelected(payload: { pdvName: string; pdvCode: string }): void {
    if (!payload.pdvName) {
      console.warn('[onStoreSelected] PDV sin pdv_name — skip');
      return;
    }
    this.storeSelected$.next(payload);
  }
  ngOnChanges(): void { if (this.boardConfig) this.bootstrap(); }

  ngAfterViewInit(): void {
    if (this.paginator) this.detailDataSource.paginator = this.paginator;
  }

  // ── Carga inicial ────────────────────────────────────────────────────────
  private bootstrap(): void {
    if (!this.boardId) { this.configError = true; return; }

    this.configSvc.getCodeConfig(this.boardId).subscribe({
      next: cfg => {
        
        // ── Resolver filtros board_param desde los parámetros del tablero ──
        const raw = this.svc.currentBoardData?.parameters ?? {};
        const uid_actual = this.svc.currentUid;
        // Solo los parametros del usuario de la sesion. Antes se aplanaban todos
        // los uid con Object.assign, y para una misma clave ganaba el ultimo del
        // mapa en vez del usuario logueado.
        const boardParams = this.svc.getParamsForUser(raw, uid_actual);

        this.missingParams = [];
        this.filterValues  = {};

        cfg.filters
          .filter(f => f.source === 'board_param')
          .forEach(f => {
            const val = boardParams[f.paramKey!];
            if (!val && f.required) {
              this.missingParams.push(f.label ?? f.paramKey ?? f.key);
            }
            this.filterValues[f.key] = val ? String(val) : '';
          });

        if (this.missingParams.length) return; // template muestra el error

        // Inicializar filtros from_card y static con valor por defecto
        cfg.filters
          .filter(f => f.source !== 'board_param')
          .forEach(f => {
            if (!(f.key in this.filterValues)) {
              this.filterValues[f.key] = 'all';
            }
          });

        // Filtros 'static' traen sus opciones fijas en la config (no dependen de un card)
        cfg.filters
          .filter(f => f.source === 'static')
          .forEach(f => { this.selectOptions[f.key] = f.options ?? []; });

        this.boardConfig  = cfg;
        this.logoUrl      = cfg.banner.logoUrl;
        this.logoSubtitle = cfg.banner.subtitleFixed;
        this.logoBadge    = cfg.banner.badgeText ?? '';
        // Título: primer board_param resuelto, o nombre del tablero
        const firstBoardVal = cfg.filters.find(f => f.source === 'board_param');
        this.logoTitle = (firstBoardVal ? this.filterValues[firstBoardVal.key] : '') || cfg.name;
        this.loadedTabs = new Set();
        // Load first tab (resumen if present, else first tab)
        const firstTabId = cfg.tabs[0]?.id ?? 'resumen';
        this.dashTab = firstTabId;
        this.loadTab(firstTabId, true);
      },
      error: () => { this.configError = true; },
    });
  }

  // ── Cambio de pestaña ────────────────────────────────────────────────────
  onDashTabChange(tabId: string): void {
    this.dashTab = tabId;
    if (!this.loadedTabs.has(tabId)) {
      this.loadTab(tabId, false);
    }
  }

  // ── Cambio de un filtro select (from_card o static) ─────────────────────
  onSelectFilterChange(filterKey: string, value: string): void {
    this.filterValues[filterKey] = value;
    const tab = this.currentTabConfig;
    if (!tab) return;
    if (tab.refreshOn.includes(filterKey)) {
      this.detailPageOffset = 0;
      this.loadTab(this.dashTab, false);
    }
  }

  /** Texto compacto "dd/mm/aaaa" para el trigger del selector de fechas */
  formatDetailDate(d: Date): string {
    return new Intl.DateTimeFormat('es-CO', { day: '2-digit', month: '2-digit', year: 'numeric' }).format(d);
  }

  onDetailDateChange(): void {
    if (this.detailDateStart && this.detailDateEnd) {
      this.detailPageOffset = 0;
      this.loadedTabs.delete('sdv-detail');
      this.loadTab('sdv-detail', false);
    }
  }

  // ── Paginación server-side de "Ejecuciones" (card 419) ──────────────────
  readonly detailPageSize = 50;
  detailPageOffset = 0;
  /** Total de filas que matchean los filtros actuales (viene de total_count / COUNT(*) OVER()) */
  detailTotalRows: number | null = null;
  /** true si hay una página siguiente disponible */
  detailHasNextPage = false;

  get detailCurrentPage(): number {
    return Math.floor(this.detailPageOffset / this.detailPageSize) + 1;
  }

  get detailTotalPages(): number | null {
    return this.detailTotalRows !== null
      ? Math.max(1, Math.ceil(this.detailTotalRows / this.detailPageSize))
      : null;
  }

  goToPrevPage(): void {
    if (this.detailPageOffset <= 0) return;
    this.detailPageOffset = Math.max(0, this.detailPageOffset - this.detailPageSize);
    this.loadTab('sdv-detail', false);
  }

  goToNextPage(): void {
    if (!this.detailHasNextPage) return;
    this.detailPageOffset += this.detailPageSize;
    this.loadTab('sdv-detail', false);
  }

  // ── Carga de una pestaña completa ────────────────────────────────────────
  private loadTab(tabId: string, isInitial: boolean): void {
    const tab = this.boardConfig?.tabs.find(t => t.id === tabId);
    if (!tab) return;

    // Marcar slots como loading
    tab.cards.forEach(c => this.loadingSlots[c.id] = true);
    if (isInitial) this.loadingBanner = true;

    // Recopilar todos los queries únicos por cardId
    const allQueries = tab.cards.flatMap(c => c.queries);
    const uniqueCardIds = [...new Set(allQueries.map(q => q.cardId))];

    // Si la pestaña no tiene queries (ej. geo-heatmap con datos simulados),
    // ejecutar el transform directamente sin llamadas al servidor.
    if (!uniqueCardIds.length) {
      tab.cards.forEach(c => {
        this.applyTransform(c, {}, false);
        this.loadingSlots[c.id] = false;
      });
      return;
    }

    // Para cada cardId único, construir params del PRIMER query que lo usa
    // (queries del mismo cardId en distintas cards comparten los params de server)
    const requests: Record<number, any> = {};
    uniqueCardIds.forEach(cardId => {
      const queryDef = allQueries.find(q => q.cardId === cardId)!;
      const params   = this.buildParams(queryDef.params);
      requests[cardId] = this.svc.getMetabaseCardJson(cardId, params).pipe(
        catchError(() => of([])),
      );
    });

    forkJoin(requests).subscribe({
      next: (rawById: Record<number, any>) => {
        // ── Poblar opciones de filtros from_card ────────────────────────────
        // Se repuebla en cada carga (no solo la inicial) para que selects como
        // "Misión" —cuyas opciones dependen del RDV seleccionado— se actualicen
        // cuando el cardId que los alimenta se vuelve a consultar.
        (this.boardConfig?.filters ?? [])
          .filter(f => f.source === 'from_card' && f.cardId && f.field && rawById[f.cardId!] !== undefined)
          .forEach(f => {
            const rows = (rawById[f.cardId!] as Record<string, any>[]) ?? [];
            this.rawFilterData[f.key] = rows;
            this.selectOptions[f.key] = [...new Set(
              rows.map(r => String(r[f.field!] ?? '')).filter(Boolean)
            )].sort();
          });

        // Aplicar cada transform por card
        tab.cards.forEach(card => {
          const dataMap: Record<string, Record<string, any>[]> = {};
          card.queries.forEach(q => {
            dataMap[q.alias] = (rawById[q.cardId] as Record<string, any>[]) ?? [];
          });
          this.applyTransform(card, dataMap, isInitial);
          this.loadingSlots[card.id] = false;
        });

        if (isInitial) this.loadingBanner = false;
        this.loadedTabs.add(tabId);
      },
      error: () => {
        tab.cards.forEach(c => this.loadingSlots[c.id] = false);
        this.errorTop = true;
      },
    });
  }

  // ── Build params desde la definición del query ───────────────────────────
  private buildParams(paramDefs: QueryParam[]): object[] {
    const params: object[] = [];
    const fmt = (d: Date) => d.toISOString().slice(0, 10);

    for (const p of paramDefs) {
      const value = this.resolveFilterValue(p.filterKey);

      // Filtros opcionales sin valor → omitir
      if (p.optional && (!value || value === 'all')) continue;

      // ── date/range ──────────────────────────────────────────────────────
      if (p.paramType === 'date/range') {
        params.push({
          id:     p.templateTagId ?? p.templateTag,
          type:   'date/range',
          value:  `${fmt(this.detailDateStart)}~${fmt(this.detailDateEnd)}`,
          target: ['dimension', ['template-tag', p.templateTag]],
        });
        continue;
      }

      // ── date/single-start → usa detailDateStart ─────────────────────────
      if (p.paramType === 'date/single-start') {
        params.push({
          id:     p.templateTagId ?? p.templateTag,
          type:   'date/single',
          value:  fmt(this.detailDateStart),
          target: [p.targetType, ['template-tag', p.templateTag]],
        });
        continue;
      }

      // ── date/single-end → usa detailDateEnd ─────────────────────────────
      if (p.paramType === 'date/single-end') {
        params.push({
          id:     p.templateTagId ?? p.templateTag,
          type:   'date/single',
          value:  fmt(this.detailDateEnd),
          target: [p.targetType, ['template-tag', p.templateTag]],
        });
        continue;
      }

      // ── number/= con valor estático, o dinámico para paginación (offset) ──
      if (p.paramType === 'number/=') {
        const numValue = p.staticValue !== undefined
          ? p.staticValue
          : p.filterKey === 'offset'
            ? this.detailPageOffset
            : 0;
        params.push({
          id:     p.templateTagId ?? p.templateTag,
          type:   'number/=',
          value:  [String(numValue)],
          target: [p.targetType, ['template-tag', p.templateTag]],
        });
        continue;
      }

      // ── string/* ────────────────────────────────────────────────────────
      params.push({
        id:      p.templateTagId ?? p.templateTag,
        type:    p.paramType,
        value:   value ? [value] : null,
        target:  [p.targetType, ['template-tag', p.templateTag]],
        ...(p.paramType === 'string/contains' ? { options: { 'case-sensitive': false } } : {}),
      });
    }
    return params;
  }

  private resolveFilterValue(filterKey: string): string {
    if (filterKey === 'dateRange') return ''; // manejado aparte en buildParams
    return this.filterValues[filterKey] ?? '';
  }

  // ── Registry de transforms ───────────────────────────────────────────────
  private applyTransform(
    card: DashCardConfig,
    data: Record<string, Record<string, any>[]>,
    isInitial: boolean,
  ): void {
    switch (card.transform) {

      case 'kpiStatusCard': {
        const kpiRow   = data['kpi']?.[0]  ?? {};
        const data329  = data['cedi']      ?? [];
        const cediObj  = data329.reduce((s, r) => s + Number(r['OBJ']), 0);
        const cediAprob = data329.reduce((s, r) => s + Number(r['APROBADOS']), 0);
        const cediCump = cediObj > 0
          ? (cediAprob / cediObj * 100).toFixed(1) + ' %'
          : '0 %';
        const myObj    = Number(kpiRow['OBJ'])        || 0;
        const myAprob  = Number(kpiRow['APROBADOS'])  || 0;
        const myCump   = String(kpiRow['Porcentaje_CUMP'] ?? '0 %');
        const myPct    = Number(myCump.replace('%', '').trim().replace(',', '.')) || 0;

        if (card.updatesBanner && isInitial) {
          this.logoStatusColor = myPct < 30 ? '#ef4444' : myPct < 70 ? '#f59e0b' : '#22c55e';
          this.logoStatusLabel = myPct < 30 ? 'Bajo'    : myPct < 70 ? 'Medio'   : 'Alto';
          this.logoCompliancePercent = Math.max(0, Math.min(100, myPct));
        }

        this.kpiStatusCard = {
          title: '', subtitle: '',
          groups: [
            {
              label: 'Mis Objetivos', icon: 'pi-user', accent: '#6366f1',
              cumplimiento: myCump,
              stats: [
                { label: 'Mi Objetivo',     value: myObj.toLocaleString('es-CO') },
                { label: 'Mi Ejecución',    value: myAprob.toLocaleString('es-CO') },
                { label: 'Mi Cumplimiento', value: myCump },
              ],
            },
            {
              label: 'Objetivos CEDI', icon: 'pi-building', accent: '#a78bfa',
              cumplimiento: cediCump,
              stats: [
                { label: 'Obj. Total',    value: cediObj.toLocaleString('es-CO') },
                { label: 'Ejec. Total',   value: cediAprob.toLocaleString('es-CO') },
                { label: 'Cumplimiento',  value: cediCump },
              ],
            },
          ],
        };
        break;
      }

      case 'trendBar': {
        const raw    = data['trend'] ?? [];
        const dayMap: Record<number, number> = {};
        raw.forEach(r => {
          const d = Number(r['Dia_Ejecucion']);
          dayMap[d] = (dayMap[d] ?? 0) + Number(r['Aprobado']);
        });
        this.trendData = Object.entries(dayMap)
          .sort(([a], [b]) => Number(a) - Number(b))
          .map(([day, aprobados]) => ({ dia: `Día ${day}`, aprobados }));

        if (card.updatesBannerSparkline && isInitial) {
          this.logoTrend = this.trendData.map(d => d.aprobados);
        }

        this.trendChartCard = {
          title: 'Tendencia diaria · Ejecuciones',
          chartType: 'cj_bar',
          labelKey: 'dia', valueKey: 'Ejecuciones',
          barColor: '#d9468fcc', barBorderColor: '#d9468f',
          hideYTicks: true,
          data: this.trendData.map(r => ({ dia: r.dia.replace('Día ', ''), Ejecuciones: r.aprobados })),
        };
        break;
      }

      case 'missionCards': {
        const raw = data['missions'] ?? [];
        const missions = raw
          .map(r => ({
            Mision:         String(r['Mision'] ?? ''),
            Ejecuciones:    Number(r['APROBADOS'] ?? 0),
            Objetivos:      Number(r['OBJ'] ?? 0),
            Porcentaje_CUMP: Number(r['OBJ'] ?? 0) > 0
              ? Number(r['APROBADOS'] ?? 0) / Number(r['OBJ'] ?? 0)
              : 0,
          }))
          .filter(r => r.Mision)
          .sort((a, b) => b.Porcentaje_CUMP - a.Porcentaje_CUMP)
          .slice(0, 12);

        this.missionChartCard = {
          title: 'Cumplimiento por Misión',
          chartType: 'cj_bar_h',
          labelKey: 'Mision', valueKey: 'Ejecuciones', value2Key: 'Objetivos',
          barColor: '#d9468fcc', barBorderColor: '#d9468f',
          data: missions,
        };

        const ICONS = ['pi-star','pi-bolt','pi-trophy','pi-verified','pi-crown',
                       'pi-thumbs-up','pi-flag','pi-heart','pi-check-circle','pi-fire',
                       'pi-award','pi-shield'];
        const badges: AchievementBadge[] = missions.map((m, i) => {
          const pct = m.Porcentaje_CUMP * 100;
          return {
            id: `mission-${i}`,
            label: m.Mision,
            description: `${m.Ejecuciones.toLocaleString('es-CO')} / ${m.Objetivos.toLocaleString('es-CO')} ejecuciones`,
            icon: ICONS[i % ICONS.length],
            color: pct >= 100 ? '#4ade80' : pct >= 75 ? '#fb923c' : '#6366f1',
            unlocked: pct >= 100,
            unlockedAt: `${pct.toFixed(1)}%`,
          };
        });
        this.achievementCard = { title: 'Cumplimiento por Misión', headerIcon: 'pi-trophy', badges };
        break;
      }

      case 'rdvCombo': {
        // Buscar el filtro from_card local que alimenta este transform
        const localFilter = this.boardConfig?.filters.find(
          f => f.source === 'from_card' && f.mode === 'local'
        );
        const filterKey  = localFilter?.key  ?? 'rdv';
        const filterField = localFilter?.field ?? 'RDV';
        const base        = this.rawFilterData[filterKey] ?? data[card.queries[0]?.alias] ?? [];
        const activeVal   = this.filterValues[filterKey] ?? 'all';
        const filtered = activeVal === 'all'
          ? base
          : base.filter(r => String(r[filterField]) === activeVal);

        const rows = filtered
          .map(r => {
            const EJEC = Number(r['EJEC'] ?? 0);
            const OBJ  = Number(r['OBJ']  ?? 0);
            return {
              [filterField]:    String(r[filterField] ?? ''),
              EJEC,
              OBJ,
              Porcentaje_CUMP: OBJ > 0 ? EJEC / OBJ : 0,
            };
          })
          .filter(r => r[filterField])
          .sort((a, b) => b.EJEC - a.EJEC)
          .slice(0, 12);

        this.rdvChartCard = {
          title: `Cumplimiento por ${filterField}`,
          chartType: 'cj_combo',
          labelKey: filterField, valueKey: 'EJEC', value2Key: 'OBJ',
          comboStyle: 'solid',
          data: rows,
        };
        break;
      }

      case 'rankingCombo': {
        const raw = data['ranking'] ?? [];
        if (!raw.length) { this.rankingChartCard = null; break; }

        const sample  = raw[0];
        const keys    = Object.keys(sample);
        const labelKey = keys.find(k => typeof sample[k] === 'string' && k !== 'sales_force')
          ?? keys.find(k => typeof sample[k] === 'string')
          ?? keys[0];
        const numKeys  = keys.filter(k => !Number.isNaN(Number(sample[k])));
        const valueKey  = numKeys[0];
        const value2Key = numKeys[1];

        if (!labelKey || !valueKey) { this.rankingChartCard = null; break; }

        this.rankingChartCard = {
          title: 'Ranking mejores del CEDI',
          chartType: value2Key ? 'cj_combo' : 'cj_bar_h',
          labelKey, valueKey, value2Key,
          comboStyle: 'solid',
          hideYTicks: true, hideY2Ticks: true,
          data: raw,
        };
        break;
      }

      case 'executionList': {
        const rows = (data['detail'] ?? []) as Record<string, any>[];
        this.detailSdvRows        = rows;
        this.detailSdvDisplayKeys = this.buildDisplayKeys(rows);
        this.detailDataSource.data = rows;
        if (this.paginator) this.detailDataSource.paginator = this.paginator;
        this.loadedSdvDetail = true;

        // total_count viene de COUNT(*) OVER() en el query 419 (misma en todas las filas)
        const rawTotal = rows[0]?.['total_count'];
        this.detailTotalRows = rawTotal !== undefined ? Number(rawTotal) : null;
        this.detailHasNextPage = this.detailTotalRows !== null
          ? (this.detailPageOffset + rows.length) < this.detailTotalRows
          : rows.length === this.detailPageSize; // fallback heurístico si el query aún no trae total_count
        break;
      }

      // ── Reconocimiento: dos gráficas desde card 431 ──────────────────────
      case 'taskSummaryBar': {
        const raw = (data['recs'] ?? []) as Record<string, any>[];

        // Limpiar slots no usados en este tablero
        this.kpiStatusCard    = null;
        this.missionChartCard = null;
        this.achievementCard  = null;
        this.rankingChartCard = null;

        // ── Gráfica 1: Aprobados vs Total por task_name → rdvChartCard ──────
        const taskMap: Record<string, { total: number; approved: number; scoreSum: number }> = {};
        raw.forEach(r => {
          const name = String(r['task_name'] ?? r['task_label'] ?? 'Sin nombre');
          if (!taskMap[name]) taskMap[name] = { total: 0, approved: 0, scoreSum: 0 };
          taskMap[name].total++;
          taskMap[name].scoreSum += Number(r['recognition_task_score'] ?? 0);
          if (r['recognition_task_approved'] === true || r['recognition_task_approved'] === 'true') {
            taskMap[name].approved++;
          }
        });

        const taskRows = Object.entries(taskMap)
          .map(([task, v]) => ({
            Tarea:     task,
            Aprobados: v.approved,
            Total:     v.total,
          }))
          .sort((a, b) => b.Total - a.Total)
          .slice(0, 12);

        this.rdvChartCard = {
          title:       'Aprobados vs Total por Tarea',
          chartType:   'cj_combo',
          labelKey:    'Tarea',
          valueKey:    'Aprobados',
          value2Key:   'Total',
          comboStyle:  'solid',
          hideYTicks:  true,
          hideY2Ticks: true,
          data: taskRows,
        };

        // ── Gráfica 2: Ejecuciones por día → trendChartCard ─────────────────
        const dayMap: Record<string, { total: number; approved: number }> = {};
        raw.forEach(r => {
          const rawDate = r['recognition_created_at'] ?? r['event_created_at'];
          if (!rawDate) return;
          const day = String(rawDate).slice(0, 10); // YYYY-MM-DD
          if (!dayMap[day]) dayMap[day] = { total: 0, approved: 0 };
          dayMap[day].total++;
          if (r['recognition_task_approved'] === true || r['recognition_task_approved'] === 'true') {
            dayMap[day].approved++;
          }
        });

        const dayRows = Object.entries(dayMap)
          .sort(([a], [b]) => a.localeCompare(b))
          .map(([day, v]) => ({
            Fecha:     day.slice(5).replace('-', '/'), // MM/DD
            Total:     v.total,
            Aprobados: v.approved,
          }));

        this.trendChartCard = {
          title:          'Ejecuciones por día',
          chartType:      'cj_bar',
          labelKey:       'Fecha',
          valueKey:       'Total',
          barColor:       '#6366f1cc',
          barBorderColor: '#6366f1',
          hideYTicks:     true,
          data: dayRows,
        };

        break;
      }

      // ── Objetos detectados: parsea objetos_detectados de cada fila ──────────
      case 'detectedObjectsList': {
        const raw = (data['recs'] ?? []) as Record<string, any>[];

        // Mapa key `tag::name` → acumulador
        const accum: Record<string, { tag: string; name: string; count: number; scoreSum: number }> = {};

        for (const row of raw) {
          let parsed: any[] = [];
          try {
            const raw_val = row['objetos_detectados'];
            parsed = typeof raw_val === 'string' ? JSON.parse(raw_val) : (Array.isArray(raw_val) ? raw_val : []);
          } catch { /* fila sin JSON válido */ }

          for (const obj of parsed) {
            const tag   = String(obj['tag']  ?? 'otros').toLowerCase();
            const name  = String(obj['library_object_name'] ?? 'Desconocido');
            const score = Number(obj['score'] ?? 0);
            const key   = `${tag}::${name}`;
            if (!accum[key]) accum[key] = { tag, name, count: 0, scoreSum: 0 };
            accum[key].count++;
            accum[key].scoreSum += score;
          }
        }

        const items: DetectedObjectItem[] = Object.values(accum).map(a => ({
          name:     a.name,
          tag:      a.tag,
          count:    a.count,
          avgScore: a.count > 0 ? a.scoreSum / a.count : 0,
        }));

        this.detectedObjectsCard = {
          title: 'Objetos Detectados',
          items,
        };
        break;
      }

      // ── Helper interno: parsea objetos_detectados de un array de filas ─────
      // (usado por bboxGallery y detectionHeatmap)

      // ── Galería con bounding boxes ────────────────────────────────────────
      case 'bboxGallery': {
        const rows = this.parseRecognitionRows((data['recs'] ?? []) as Record<string, any>[])
          .filter(r => !!r.imageUrl);
        this.bboxGalleryCard = { title: 'Galería de detecciones', rows };
        break;
      }

      // ── Planograma virtual ────────────────────────────────────────────────
      case 'planogramView': {
        const rows = this.parseRecognitionRows((data['recs'] ?? []) as Record<string, any>[]);
        this.planogramCard = { title: 'Planograma virtual', rows };
        break;
      }

      // ── Cobertura de área ─────────────────────────────────────────────────
      case 'areaCoverage': {
        const rows = this.parseRecognitionRows((data['recs'] ?? []) as Record<string, any>[]);
        this.areaCoverageCard = { title: 'Cobertura de área', rows };
        break;
      }

      // ── Mapa de calor geográfico ──────────────────────────────────────────
      case 'storeHeatmap': {
        // Query 435 → lista de PDV con geolocalización (puntos del mapa)
        // Query 436 → catálogo de library_object_id → nombre de producto
        // El detalle por PDV se carga lazy con query 431 al hacer clic.
        const raw     = (data['stores']  ?? []) as Record<string, any>[];
        const recs    = (data['recs']    ?? []) as Record<string, any>[];
        const catRows = (data['catalog'] ?? []) as Record<string, any>[];

        // Construir catálogo: Map<library_object_id, CatalogEntry>
        // Query 436 devuelve nombre + (posiblemente) atributos del objeto.
        // Los atributos pueden venir como columnas planas (brand, category, size,
        // company) o como un array JSON en la columna 'attributes'.
        const catalog = new Map<number, CatalogEntry>();
        catRows.forEach(r => {
          const id   = Number(r['library_object_id']);
          const name = String(r['library_object_name'] ?? '');
          if (!id || !name) return;

          let attrs: Array<{ attribute_type_name: string; attribute_name: string }> = [];
          const rawAttrs = r['attributes'];
          if (Array.isArray(rawAttrs)) {
            attrs = rawAttrs.map((a: any) => ({
              attribute_type_name: String(a['attribute_type_name'] ?? ''),
              attribute_name:      String(a['attribute_name']      ?? ''),
            }));
          } else if (typeof rawAttrs === 'string' && rawAttrs.trim().startsWith('[')) {
            try { attrs = JSON.parse(rawAttrs); } catch { /* JSON inválido */ }
          } else {
            // Columnas planas — patrón alternativo que puede adoptar query 436
            (['brand', 'category', 'size', 'company'] as const).forEach(col => {
              const val = r[col] ?? r[`attribute_${col}`];
              if (val != null && String(val).trim()) {
                attrs.push({ attribute_type_name: col, attribute_name: String(val) });
              }
            });
          }
          catalog.set(id, { name, attributes: attrs });
        });

        const detailsByStore = this.buildStoreDetailsFrom432(recs, catalog);
        const resolvedStores = raw.length ? this.buildStoresFromPdvList(raw) : undefined;

        this.storeHeatmapCard = {
          title:   'Mapa de tiendas',
          stores:  resolvedStores,
          detailsByStore,
          catalog,
        };
        this.storeHeatmapCardAlt = {
          title:   'Mapa de hallazgos — Vacíos, contaminantes y caras',
          stores:  resolvedStores,
          detailsByStore,
          catalog,
        };
        break;
      }

      // ── Análisis de nevera ───────────────────────────────────────────────
      case 'fridgeAnalysis': {
        const rows = this.parseRecognitionRows((data['recs'] ?? []) as Record<string, any>[]);
        this.fridgeAnalysisCard = this.buildFridgeAnalysis(rows);
        break;
      }

      // ── Contaminación ────────────────────────────────────────────────────
      case 'contaminationAnalysis': {
        const rows = this.parseRecognitionRows((data['recs'] ?? []) as Record<string, any>[]);
        this.contaminationCard = this.buildContaminationAnalysis(rows);
        break;
      }

      // ── KPI Postobon vs Competencia ───────────────────────────────────────
      case 'recognitionKpi': {
        const raw  = (data['recs'] ?? []) as Record<string, any>[];
        const rows = this.parseRecognitionRows(raw);
        this.recognitionKpiCard = { title: 'Análisis de presencia', rows };
        break;
      }

      // ── Tabla de confianza por objeto ─────────────────────────────────────
      case 'confidenceTable': {
        const raw  = (data['recs'] ?? []) as Record<string, any>[];
        const rows = this.parseRecognitionRows(raw);
        this.confidenceTableCard = { title: 'Confianza por objeto', rows };
        break;
      }

      // ── Mapa de calor acumulado ───────────────────────────────────────────
      case 'detectionHeatmap': {
        const rows = this.parseRecognitionRows((data['recs'] ?? []) as Record<string, any>[]);
        this.detectionHeatmapCard = { title: 'Mapa de calor', rows };
        break;
      }

      // ── Lista de reconocimientos (query 432): normaliza columnas ───────────
      case 'recognitionList': {
        const raw = (data['detail'] ?? []) as Record<string, any>[];
        const rows = raw.map(r => ({
          ...r,
          // Alias para miniatura (la plantilla pide 'original_image_path')
          original_image_path: r['url_imagen_original'] ?? r['original_image_path'] ?? '',
          // Estado aprobado/rechazado derivado del boolean del query 432
          recognition_task_status:
            r['recognition_task_approved'] === true || r['recognition_task_approved'] === 'true'
              ? 'approved'
              : 'rejected',
          // Aliases para que getVendorLine / getTiendaLine encuentren algo
          // (query 432 no trae vendor ni store; usamos task_key como fallback)
          vendor_code: r['vendor_code'] ?? r['sdv'] ?? r['task_key'] ?? '—',
          store_name:  r['store_name']  ?? r['tienda'] ?? r['pdv_name'] ?? '—',
        }));
        this.detailSdvRows        = rows;
        this.detailSdvDisplayKeys = this.buildDisplayKeys(rows);
        this.detailDataSource.data = rows;
        if (this.paginator) this.detailDataSource.paginator = this.paginator;
        this.loadedSdvDetail = true;
        break;
      }
    }
  }

  // ── Parser compartido de objetos_detectados ──────────────────────────────
  private parseRecognitionRows(
    raw: Record<string, any>[],
    catalog?: Map<number, CatalogEntry>,
  ): RecognitionRow[] {
    return raw
      .map(r => {
        let objects: RecognitionObject[] = [];
        try {
          const v = r['objetos_detectados'];
          const parsed: any[] = typeof v === 'string' ? JSON.parse(v) : (Array.isArray(v) ? v : []);
          objects = parsed.map((o: any) => {
            const libId        = o['library_object_id'] ? Number(o['library_object_id']) : undefined;
            const catalogEntry = (libId && catalog) ? catalog.get(libId) : undefined;
            return {
              name:  String(o['library_object_name'] ?? catalogEntry?.name ?? 'Desconocido'),
              tag:   String(o['tag'] ?? 'otros').toLowerCase(),
              score: Number(o['score'] ?? 0),
              bbox: {
                x_min:  Number(o['bounding_box']?.x_min  ?? 0),
                y_min:  Number(o['bounding_box']?.y_min  ?? 0),
                x_max:  Number(o['bounding_box']?.x_max  ?? 0),
                y_max:  Number(o['bounding_box']?.y_max  ?? 0),
                width:  Number(o['bounding_box']?.width   ?? 0),
                height: Number(o['bounding_box']?.height  ?? 0),
              },
              libraryObjectId: libId,
              // Atributos: inline si los trae el query, sino desde el catálogo 436
              attributes: (Array.isArray(o['attributes']) && o['attributes'].length)
                ? o['attributes'].map((a: any) => ({
                    attribute_type_name: String(a['attribute_type_name'] ?? ''),
                    attribute_name:      String(a['attribute_name'] ?? ''),
                  }))
                : (catalogEntry?.attributes ?? []),
            };
          });
        } catch { /* fila inválida */ }
        return { imageUrl: String(r['url_imagen_original'] ?? ''), objects };
      })
      .map(r => ({
        ...r,
        objects: r.objects.filter(o => o.name.trim().toLowerCase() !== 'others'),
      }))
      .filter(r => r.objects.length);
  }

  // ── Helpers de ejecuciones ───────────────────────────────────────────────
  private buildDisplayKeys(rows: Record<string, any>[]): string[] {
    if (!rows.length) return [];
    const blacklist = new Set([
      'original_image_path','recognition_task_status','event_uuid',
      'evnt_uuid','uuid','task_id','recognition_task_id','total_count',
    ]);
    return Object.keys(rows[0]).filter(k => !blacklist.has(k)).slice(0, 6);
  }

  private getFieldByTerms(row: Record<string, any>, terms: string[]): string {
    const key = Object.keys(row).find(k => terms.some(t => k.toLowerCase().includes(t)));
    const v   = key ? row[key] : null;
    return v != null && String(v).trim() !== '' ? String(v) : '—';
  }

  getVendorLine(row: Record<string, any>): string {
    return this.getFieldByTerms(row, ['vendorcode','vendor_code','asesor','vendedor','sales_force','sdv']);
  }

  getTiendaLine(row: Record<string, any>): string {
    return this.getFieldByTerms(row, ['tienda','store_name','store','shop','cliente']);
  }

  getMisionLine(row: Record<string, any>): string {
    return this.getFieldByTerms(row, ['mision','mission','task_name','taskname','nombre']);
  }

  getAsesorTiendaLine(row: Record<string, any>): string {
    return `${this.getVendorLine(row)} · ${this.getTiendaLine(row)}`;
  }

  getFechaBonitaLine(row: Record<string, any>): string {
    const raw = this.getFieldByTerms(row, ['fecha','date','created_at','timestamp','dia','day']);
    if (raw === '—') return raw;
    const d = new Date(raw);
    if (Number.isNaN(d.getTime())) return raw;
    return new Intl.DateTimeFormat('es-CO', {
      year:'numeric', month:'2-digit', day:'2-digit',
      hour:'2-digit', minute:'2-digit',
    }).format(d);
  }

  formatColumnName(key: string): string {
    const map: Record<string, string> = {
      vendor_code: 'Cód. Vendedor', store_name: 'Tienda', task_name: 'Misión',
      recognition_created_at: 'Fecha Creación', recognition_updated_at: 'Fecha Actualización',
      recognition_task_status: 'Estado', store_id: 'ID Tienda', vendor_id: 'ID Vendedor',
      task_id: 'ID Tarea', vendorcode: 'Cód. Vendedor', Dia_Ejecucion: 'Día Ejecución',
      Aprobado: 'Aprobado', OBJ: 'Objetivo', EJEC: 'Ejecución',
      Porcentaje_CUMP: '% Cump.', Mision: 'Misión', RDV: 'RDV',
      fecha_rango: 'Fecha', tienda: 'Tienda', cliente: 'Cliente', fecha: 'Fecha',
    };
    return map[key] ?? key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  }

  detailStatusLabel(status: string | boolean | null | undefined): string {
    return this.detailStatusClass(status) === 'is-ok' ? 'Aprobado' : 'No aprobado';
  }

  detailStatusShort(status: string | boolean | null | undefined): string {
    return this.detailStatusClass(status) === 'is-ok' ? 'Apro.' : 'No apro.';
  }

  detailStatusClass(status: string | boolean | null | undefined): string {
    if (status === true)  return 'is-ok';
    if (status === false) return 'is-danger';
    const s = String(status ?? '').toLowerCase();
    if (!s || s === 'null' || s === 'undefined') return 'is-danger';
    if (s === 'true' || s === 'approved') return 'is-ok';
    if (s === 'false' || s === 'rejected') return 'is-danger';
    if (s.includes('apro') || s.includes('comp') || s.includes('ok') ||
        s.includes('done') || s.includes('ejec') || s.includes('success') ||
        s.includes('valid') || s.includes('activ')) return 'is-ok';
    return 'is-danger';
  }

  // ── Dialog ───────────────────────────────────────────────────────────────
  openEventDialog(row: any): void {
    const uuid = row['event_uuid'] || row['evnt_uuid'] || row['uuid']
               || row['eventId'] || row['event_id'] || row['recognition_id'] || null;
    this.currentDialogRef = this.dialog.open(this.eventDialogTemplate, {
      data: { uuid, row },
      width: '100vw', maxWidth: '100vw',
      height: '100vh', maxHeight: '100vh',
      panelClass: 'cd-visualizer-dialog-panel',
    });
  }

  // ── Getters de conveniencia para la plantilla ────────────────────────────
  get currentTabConfig(): TabConfig | undefined {
    return this.boardConfig?.tabs.find(t => t.id === this.dashTab);
  }

  get tabs(): TabConfig[] {
    return this.boardConfig?.tabs ?? [];
  }

  /**
   * Filtros que deben mostrarse como select en la UI (from_card o static),
   * acotados a la pestaña activa: solo se muestran los que esa pestaña
   * declara en su `refreshOn` (ej. "Misión"/"Estado" solo pertenecen a Ejecuciones).
   */
  get selectableFilters(): BoardFilter[] {
    const activeRefresh = this.currentTabConfig?.refreshOn ?? [];
    return (this.boardConfig?.filters ?? []).filter(
      f => (f.source === 'from_card' || f.source === 'static') && activeRefresh.includes(f.key)
    );
  }

  /** ¿La pestaña activa tiene un filtro dateRange en sus cards? */
  get activeTabHasDateRange(): boolean {
    return this.currentTabConfig?.refreshOn.includes('dateRange') ?? false;
  }

  isLoading(cardId: string): boolean {
    return this.loadingSlots[cardId] ?? false;
  }

  get displayedColumns(): string[] {
    return ['imagen', 'estado', ...this.detailSdvDisplayKeys];
  }

  /**
   * Normaliza el nombre de una ciudad colombiana.
   * El backend devuelve variantes inconsistentes: "Medellín", "Medellin",
   * "BOGOTA", "bogotá", etc. — todas deben mapearse a un nombre canónico.
   *
   * Estrategia:
   *  1) Eliminar tildes, pasar a lowercase, trim → clave de búsqueda
   *  2) Buscar en CANONICAL_CITIES → devuelve el nombre canónico
   *  3) Si no está en el diccionario, capitalize el original (fallback)
   */
  private static readonly CANONICAL_CITIES: Record<string, string> = {
    'bogota':         'Bogotá',
    'medellin':       'Medellín',
    'cali':           'Cali',
    'barranquilla':   'Barranquilla',
    'cartagena':      'Cartagena',
    'bucaramanga':    'Bucaramanga',
    'pereira':        'Pereira',
    'manizales':      'Manizales',
    'cucuta':         'Cúcuta',
    'ibague':         'Ibagué',
    'santa marta':    'Santa Marta',
    'pasto':          'Pasto',
    'monteria':       'Montería',
    'villavicencio':  'Villavicencio',
    'armenia':        'Armenia',
    'neiva':          'Neiva',
    'popayan':        'Popayán',
    'valledupar':     'Valledupar',
    'sincelejo':      'Sincelejo',
    'tunja':          'Tunja',
    'florencia':      'Florencia',
    'yopal':          'Yopal',
    'rionegro':       'Rionegro',
    'envigado':       'Envigado',
    'sabaneta':       'Sabaneta',
    'itagui':         'Itagüí',
    'bello':          'Bello',
    'soledad':        'Soledad',
    'soacha':         'Soacha',
    'chia':           'Chía',
  };

  /**
   * Sufijos de zona que el backend agrega a veces a las ciudades:
   *   "Bogotá Norte", "Bogotá Sur", "Medellín Centro"
   * Estos NO son parte del nombre — se eliminan para mapear todo a la raíz.
   */
  private static readonly CITY_SUFFIXES = [
    'norte', 'sur', 'centro', 'oriente', 'occidente',
    'este', 'oeste', 'noroccidente', 'nororiente',
    'suroccidente', 'suroriente', 'principal', 'capital', 'd c', 'dc',
  ];

  private normalizeCity(raw: any): string {
    if (raw == null || raw === '') return 'Sin ciudad';
    const str = String(raw).trim();
    if (!str) return 'Sin ciudad';

    // Lowercase, sin tildes, espacios colapsados
    const cleaned = str
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/\s+/g, ' ')
      .trim();

    // 1) Match exacto en el diccionario canónico
    const exact = CodeDashboardComponent.CANONICAL_CITIES[cleaned];
    if (exact) return exact;

    // 2) Quitar sufijos de zona (norte/sur/centro/...) y reintentar
    //    Captura también sub-sufijos al final: "Bogota Sur 2", "Bogota Norte Zona 1"
    const suffixPattern = new RegExp(
      `\\s+(${CodeDashboardComponent.CITY_SUFFIXES.join('|')})(\\s+[\\w\\d]+)*$`
    );
    const withoutSuffix = cleaned.replace(suffixPattern, '').trim();
    if (withoutSuffix !== cleaned && withoutSuffix) {
      const fromSuffix = CodeDashboardComponent.CANONICAL_CITIES[withoutSuffix];
      if (fromSuffix) return fromSuffix;
    }

    // 3) Probar progresivamente con las primeras palabras
    //    "Bogota DC" → "bogota dc" → "bogota" ✓
    const parts = cleaned.split(' ');
    for (let i = parts.length - 1; i > 0; i--) {
      const candidate = parts.slice(0, i).join(' ');
      if (CodeDashboardComponent.CANONICAL_CITIES[candidate]) {
        return CodeDashboardComponent.CANONICAL_CITIES[candidate];
      }
    }

    // 4) Fallback: capitalize palabra por palabra del original
    return str
      .toLowerCase()
      .replace(/\b\w/g, c => c.toUpperCase());
  }

  /**
   * Limpia y parsea coordenadas del query 435.
   *
   * El backend devuelve formatos variadísimos:
   *   "4.6951"          → ya bien formateado
   *   "6.2669000"       → bien formateado, decimales largos
   *   "62,473,276"      → comas como miles, sin punto (Medellín lat → 6.2473276)
   *   "46,957,847"      → mismo caso (Bogotá → 4.6957847)
   *   "62,557.00"       → mix comas+".00" (→ 6.2557)
   *   "62,729.00"       → idem (→ 6.2729)
   *   "-755,905,227"    → negativo grande (lng → -75.5905227)
   *   "-756,034.00"     → negativo con decimal (→ -75.6034)
   *
   * Estrategia:
   *   1. Eliminar TODO separador (comas, puntos) → un único entero
   *   2. Probar shifts decimales 0..9 y elegir el primero que caiga
   *      dentro de los bounds geográficos de Colombia (área de uso real).
   *
   * Bounds Colombia (con margen):
   *   lat: 0.5 a 13 (Cabo de la Vela está a ~12.5)
   *   lng abs: 65 a 80 (Inírida a costa Pacífica)
   *
   * Devuelve { value, strategy } donde strategy explica qué shift se aplicó.
   */
  private parseCoord(raw: any, type: 'lat' | 'lng'): { value: number | null; strategy: string } {
    if (raw == null || raw === '') return { value: null, strategy: 'empty' };

    // Bounds de Colombia (área válida para PDV)
    const absMin = type === 'lat' ? 0.5 : 65;
    const absMax = type === 'lat' ? 13  : 80;

    // ── Caso 1: ya es número ───────────────────────────────────────────────
    if (typeof raw === 'number') {
      if (!isFinite(raw)) return { value: null, strategy: 'not-finite' };
      const abs = Math.abs(raw);
      if (abs >= absMin && abs <= absMax) return { value: raw, strategy: 'number-ok' };
      // Number gigante: probar shifts
      for (let shift = 1; shift <= 9; shift++) {
        const v = raw / Math.pow(10, shift);
        if (Math.abs(v) >= absMin && Math.abs(v) <= absMax) {
          return { value: v, strategy: `number/shift=${shift}` };
        }
      }
      return { value: null, strategy: `number-out-of-bounds(${raw})` };
    }

    const str = String(raw).trim();
    if (!str) return { value: null, strategy: 'empty-string' };

    const isNeg = str.startsWith('-');
    const noSign = isNeg ? str.slice(1) : str;

    // Eliminar TODO lo que no sea dígito → un único entero
    const allDigits = noSign.replace(/[^0-9]/g, '');
    if (!allDigits) return { value: null, strategy: `no-digits(${str})` };

    const rawNum = parseInt(allDigits, 10);
    if (isNaN(rawNum) || rawNum === 0) {
      return { value: null, strategy: `zero-or-nan(${str})` };
    }

    // Probar shifts 0..9 — el primero que caiga en bounds Colombia gana
    for (let shift = 0; shift <= 9; shift++) {
      const v = rawNum / Math.pow(10, shift);
      if (v >= absMin && v <= absMax) {
        const signed = isNeg ? -v : v;
        return { value: signed, strategy: `shift=${shift}` };
      }
    }

    return { value: null, strategy: `out-of-bounds(${str} → ${rawNum}, no shift válido)` };
  }

  /**
   * Construye StorePoint[] desde un query con lista de PDVs (actualmente 431).
   * Cada fila puede ser una visita/reconocimiento; agrupamos por pdv_code (o
   * pdv_name si no hay code) para tener PDVs únicos con conteos acumulados.
   */
  private buildStoresFromPdvList(rows: Record<string, any>[]): any[] {
    const byPdv = new Map<string, {
      lat: number; lng: number;
      name: string; city: string; address: string; locality: string;
      displayBrand: string; displayType: string;
      visits: number; recognitionIds: Set<number>;
      productIds: Set<number>;
      facesPtbn: number; facesCcx: number; emptyCount: number;
      raw: Record<string, any>[];
    }>();

    // Diagnóstico: contadores y muestreo de problemas
    const stats = {
      total:           rows.length,
      noPdvCode:       0,  // sin pdv_code Y sin pdv_name
      invalidLat:      0,
      invalidLng:      0,
      bothInvalid:     0,
      ok:              0,
    };
    const samplesProblemas: Array<{
      pdv_code: string;
      pdv_name: string;
      raw_lat:  any;
      raw_lng:  any;
      lat_strategy: string;
      lng_strategy: string;
    }> = [];

    rows.forEach(r => {
      const pdvCode = String(r['pdv_code'] ?? '');
      const pdvName = String(r['pdv_name'] ?? '');
      // Clave de agrupación: pdv_code si existe, sino pdv_name (muchos
      // registros del 431 tienen pdv_code vacío pero sí tienen nombre)
      const groupKey = pdvCode || pdvName;
      if (!groupKey) {
        stats.noPdvCode++;
        return;
      }

      const latRes = this.parseCoord(r['latitude'],  'lat');
      const lngRes = this.parseCoord(r['longitude'], 'lng');

      if (latRes.value == null && lngRes.value == null) stats.bothInvalid++;
      else if (latRes.value == null) stats.invalidLat++;
      else if (lngRes.value == null) stats.invalidLng++;

      if (latRes.value == null || lngRes.value == null) {
        // Guardar los primeros 10 problemas para inspección
        if (samplesProblemas.length < 10) {
          samplesProblemas.push({
            pdv_code:     pdvCode,
            pdv_name:     String(r['pdv_name'] ?? ''),
            raw_lat:      r['latitude'],
            raw_lng:      r['longitude'],
            lat_strategy: latRes.strategy,
            lng_strategy: lngRes.strategy,
          });
        }
        return;
      }

      stats.ok++;
      const city    = this.normalizeCity(r['city']);   // ⭐ normaliza tildes/case/espacios
      const address = String(r['address'] ?? '');
      const dType   = String(r['display_type']  ?? '');
      const locality = city || dType || 'Sin localidad';

      // Parsear library_object_ids_array (puede llegar como array o como string JSON)
      const rawIds = r['library_object_ids_array'];
      const rowProductIds: number[] = Array.isArray(rawIds)
        ? rawIds.map(Number).filter(Boolean)
        : (typeof rawIds === 'string' && rawIds.trim().startsWith('['))
          ? (() => { try { return (JSON.parse(rawIds) as any[]).map(Number).filter(Boolean); } catch { return []; } })()
          : [];

      const rowPtbn   = Number(r['caras_ptb']          ?? 0);
      const rowCcx    = Number(r['caras_competencia']   ?? 0);
      const rowVacios = Number(r['vacios']              ?? 0);

      const existing = byPdv.get(groupKey);
      if (existing) {
        existing.visits++;
        const recId = Number(r['recognition_id']);
        if (recId) existing.recognitionIds.add(recId);
        rowProductIds.forEach(id => existing.productIds.add(id));
        existing.facesPtbn   += rowPtbn;
        existing.facesCcx    += rowCcx;
        existing.emptyCount  += rowVacios;
        existing.raw.push(r);
      } else {
        const recId = Number(r['recognition_id']);
        byPdv.set(groupKey, {
          lat: latRes.value, lng: lngRes.value,
          name:         pdvName || pdvCode || groupKey,
          city,
          address,
          locality,
          displayBrand: String(r['display_brand'] ?? ''),
          displayType:  dType,
          visits:       1,
          recognitionIds: recId ? new Set([recId]) : new Set(),
          productIds:     new Set(rowProductIds),
          facesPtbn:   rowPtbn,
          facesCcx:    rowCcx,
          emptyCount:  rowVacios,
          raw:          [r],
        });
      }
    });

    // Diagnóstico verbose
    console.group('[storeHeatmap · query 435 (lista PDV)]');
    console.log('Filas recibidas:           ', stats.total);
    console.log('Filas sin pdv_code:        ', stats.noPdvCode);
    console.log('Filas con lat inválida:    ', stats.invalidLat);
    console.log('Filas con lng inválida:    ', stats.invalidLng);
    console.log('Filas con ambas inválidas: ', stats.bothInvalid);
    console.log('Filas OK:                  ', stats.ok);
    console.log('PDV únicos resueltos:      ', byPdv.size);
    if (samplesProblemas.length > 0) {
      console.warn('Muestras de coordenadas problemáticas (primeras 10):');
      console.table(samplesProblemas);
    }
    console.groupEnd();

    return Array.from(byPdv.values()).map(p => ({
      lat:        p.lat,
      lng:        p.lng,
      name:       p.name,
      city:       p.city,
      locality:   p.locality,
      address:    p.address,
      visits:     p.visits,
      avgScore:         0.7,
      compliance:       0.7,
      emptyCount:       p.emptyCount,
      contaminantCount: 0,
      faceCount:        p.facesPtbn + p.facesCcx,
      facesPtbn:        p.facesPtbn,
      facesCcx:         p.facesCcx,
      productIds:       Array.from(p.productIds),   // ⭐ IDs únicos de library objects
    }));
  }

  private buildStoreDetailsFrom432(
    rows: Record<string, any>[],
    catalog?: Map<number, CatalogEntry>,
  ): Record<string, StoreDetailRecord[]> {
    const byStore: Record<string, StoreDetailRecord[]> = {};
    rows.forEach((r, idx) => {
      // Si el query no devuelve store_name (ej. 432 puro), agrupar en '_all_'
      // para que el componente los muestre como pool de imágenes reales.
      // query 431 usa pdv_name; query 432 (legacy) usa store_name / tienda / store / shop
      const rawStore = r['pdv_name'] ?? r['store_name'] ?? r['tienda'] ?? r['store'] ?? r['shop'];
      const storeName = rawStore ? String(rawStore) : '_all_';
      const imageUrl = String(r['url_imagen_original'] ?? r['original_image_path'] ?? '');
      const timestamp = String(r['recognition_created_at'] ?? r['event_created_at'] ?? r['fecha'] ?? `2026-05-11 10:${String(idx % 60).padStart(2, '0')}`);
      let parsed: any[] = [];
      try {
        // query 431 llama a la columna 'objects'; query 432 y otros usan 'objetos_detectados'
        const value = r['objects'] ?? r['objetos_detectados'];
        // 1. Deserializar si viene como string JSON
        let raw: any = typeof value === 'string' ? JSON.parse(value) : value;
        // 2. Desambiguar: puede llegar como array directo [{ tag, score, ... }]
        //    o como objeto envuelto { objects: [...] } si el query cambió su estructura.
        if (raw !== null && raw !== undefined && !Array.isArray(raw)) {
          if (Array.isArray(raw['objects'])) {
            raw = raw['objects'];   // desempaquetar { objects: [...] }
          } else if (Array.isArray(raw['detections'])) {
            raw = raw['detections'];
          } else {
            // Estructura desconocida — loguear para diagnóstico
            console.warn('[buildStoreDetails] objetos_detectados inesperado (no es array):', raw);
            raw = [];
          }
        }
        parsed = Array.isArray(raw) ? raw : [];
      } catch (e) {
        console.warn('[buildStoreDetails] parse error en objetos_detectados:', e, '→ fila:', r);
        parsed = [];
      }
      if (!byStore[storeName]) byStore[storeName] = [];
      if (!parsed.length) {
        if (imageUrl) {
          byStore[storeName].push({
            imageUrl,
            tag: 'sin_tag',
            faces: 0,
            empty: 0,
            contaminant: 0,
            timestamp,
            storeName,
            objectName: 'Sin detecciones',
            score: 0,
          });
        }
        return;
      }
      parsed.forEach((obj: any) => {
        const tag      = String(obj['tag'] ?? 'otros').toLowerCase();
        // query 431 ya no embebe library_object_name ni attributes →
        // intentar resolver desde el catálogo (query 436) usando library_object_id.
        const objLibId      = obj['library_object_id'] ? Number(obj['library_object_id']) : undefined;
        const catalogEntry  = (objLibId && catalog) ? catalog.get(objLibId) : undefined;
        const objectName    = String(obj['library_object_name'] ?? catalogEntry?.name ?? obj['tag'] ?? 'Sin nombre');
        // El backend solo envía el `tag` por objeto — los flags se derivan:
        //   tag='postobon'    → es una cara de Postobon (faces=1, sino 0)
        //   tag='competencia' → es una cara de competencia (contaminant=1)
        //   tag='vacios'      → es un espacio vacío (empty=1)
        const faces       = tag === 'postobon'    ? 1 : Number(obj['faces']       ?? obj['caras']        ?? 0);
        const empty       = tag === 'vacios'      ? 1 : Number(obj['empty']       ?? obj['vacio']        ?? 0);
        const contaminant = tag === 'competencia' ? 1 : Number(obj['contaminant'] ?? obj['contaminante'] ?? 0);
        const bbox = obj['bounding_box'] ?? {};
        const xMin = Number(bbox['x_min'] ?? 0);
        const yMin = Number(bbox['y_min'] ?? 0);
        const xMax = Number(bbox['x_max'] ?? xMin);
        const yMax = Number(bbox['y_max'] ?? yMin);
        const width = Number(bbox['width'] ?? Math.max(0, xMax - xMin));
        const height = Number(bbox['height'] ?? Math.max(0, yMax - yMin));
        const score = Number(obj['score'] ?? 0.5);
        const record: StoreDetailRecord = {
          imageUrl,
          tag,
          faces,
          empty,
          contaminant,
          timestamp,
          storeName,
          objectName,
          score,
          bbox: {
            x_min: xMin,
            y_min: yMin,
            x_max: xMax,
            y_max: yMax,
            width,
            height,
          },
          // Atributos: inline si los trae el query, sino desde el catálogo 436
          attributes: (Array.isArray(obj['attributes']) && obj['attributes'].length)
            ? obj['attributes']
            : (catalogEntry?.attributes ?? []),
          event_uuid: String(r['event_uuid'] ?? ''),
        };
        byStore[storeName].push(record);
      });
    });
    return byStore;
  }

  // ── Helpers de Análisis de Nevera ─────────────────────────────────────────
  private buildFridgeAnalysis(rows: RecognitionRow[]): FridgeAnalysisCardConfig {
    const getAttr = (obj: RecognitionObject, type: string): string =>
      obj.attributes?.find(a => a.attribute_type_name === type)?.attribute_name ?? '';

    const buildPanel = (tag: 'postobon' | 'competencia'): FridgeSpacePanel => {
      const objs = rows.flatMap(r => r.objects.filter(o => o.tag === tag));

      // KPIs
      const totalFaces = objs.length;
      const avgScore   = totalFaces ? objs.reduce((s, o) => s + o.score, 0) / totalFaces : 0;

      // Categorías
      const catMap: Record<string, number> = {};
      const CAT_COLORS: Record<string, string> = {
        'Gaseosa':          '#6366f1',
        'Agua':             '#22d3ee',
        'Jugo':             '#4ade80',
        'Bebida Hidratante':'#38bdf8',
        'Bebida Energizante':'#f59e0b',
        'Cerveza':          '#fb923c',
        'Leche Saborizada': '#e879f9',
        'Suero':            '#a78bfa',
        'Sin categoria':    '#64748b',
        'Planimetria':      '#475569',
      };
      objs.forEach(o => {
        const cat = getAttr(o, 'category') || 'Sin categoria';
        catMap[cat] = (catMap[cat] ?? 0) + 1;
      });
      const categoryDist = Object.entries(catMap)
        .map(([category, count]) => ({
          category, count,
          pct: totalFaces ? (count / totalFaces) * 100 : 0,
          color: CAT_COLORS[category] ?? '#64748b',
        }))
        .filter(c => c.category !== 'Planimetria' && c.category !== 'Sin categoria' || c.count > 0)
        .sort((a, b) => b.count - a.count);

      // Empresas
      const compMap: Record<string, number> = {};
      objs.forEach(o => {
        const co = getAttr(o, 'company') || 'Sin empresa';
        compMap[co] = (compMap[co] ?? 0) + 1;
      });
      const companyDist = Object.entries(compMap)
        .map(([company, count]) => ({
          company, count,
          pct: totalFaces ? (count / totalFaces) * 100 : 0,
          isAlert: tag === 'postobon' && !company.toLowerCase().includes('postobon') && count > 0,
        }))
        .sort((a, b) => b.count - a.count);

      // Matriz marca × presentación
      const brandSizeMap: Record<string, Record<string, number>> = {};
      objs.forEach(o => {
        const brand = getAttr(o, 'brand') || 'Sin marca';
        const size  = getAttr(o, 'size')  || 'Otro';
        if (!brandSizeMap[brand]) brandSizeMap[brand] = {};
        brandSizeMap[brand][size] = (brandSizeMap[brand][size] ?? 0) + 1;
      });
      const usedSizes = new Set(objs.map(o => getAttr(o, 'size') || 'Otro'));
      const sizeColumns = CANONICAL_SIZES.filter(s => usedSizes.has(s));
      if (usedSizes.has('Otro') && !sizeColumns.includes('Otro')) sizeColumns.push('Otro');

      const brandRows = Object.entries(brandSizeMap)
        .map(([brand, sizes]) => ({
          brand, sizes,
          total: Object.values(sizes).reduce((s, n) => s + n, 0),
        }))
        .sort((a, b) => b.total - a.total)
        .slice(0, 8);

      // Grid posición 3×3
      const positionGrid: number[][] = [[0,0,0],[0,0,0],[0,0,0]];
      const refW = 1000, refH = 1500; // dimensiones de referencia aproximadas
      objs.forEach(o => {
        if (!o.bbox.width && !o.bbox.height) return;
        const cx = (o.bbox.x_min + o.bbox.x_max) / 2;
        const cy = (o.bbox.y_min + o.bbox.y_max) / 2;
        const col = cx < refW * 0.33 ? 0 : cx < refW * 0.66 ? 1 : 2;
        const row = cy < refH * 0.33 ? 0 : cy < refH * 0.66 ? 1 : 2;
        positionGrid[row][col]++;
      });
      const gridMax = Math.max(...positionGrid.flat(), 1);

      // Precios/comunicación (tag=precios con shape y company)
      const priceObjs = rows.flatMap(r => r.objects.filter(o => o.tag === 'precios'));
      const pricingTypes: Record<string, number> = {};
      priceObjs.forEach(o => {
        const shape = getAttr(o, 'shape') || 'Otro';
        if (['Precio','Cenefa','Logo'].includes(shape)) {
          pricingTypes[shape] = (pricingTypes[shape] ?? 0) + 1;
        }
      });
      const pricingCount = Object.entries(pricingTypes)
        .map(([tipo, count]) => ({ tipo, count }))
        .sort((a, b) => b.count - a.count);

      return {
        tag,
        label:       tag === 'postobon' ? 'Espacio Postobón' : 'Espacio Competencia',
        accentColor: tag === 'postobon' ? '#6366f1' : '#ef4444',
        totalFaces, avgScore, pctOfFridge: 0,  // se calcula abajo
        categoryDist, companyDist, brandRows, sizeColumns,
        positionGrid, gridMax, pricingCount,
      };
    };

    const postobonPanel    = buildPanel('postobon');
    const competenciaPanel = buildPanel('competencia');
    const total = postobonPanel.totalFaces + competenciaPanel.totalFaces || 1;
    postobonPanel.pctOfFridge    = (postobonPanel.totalFaces    / total) * 100;
    competenciaPanel.pctOfFridge = (competenciaPanel.totalFaces / total) * 100;

    // Vacíos — tag propio detectado por el modelo
    const vaciosObjs = rows.flatMap(r => r.objects.filter(o => o.tag === 'vacios'));
    const vaciosGrid: number[][] = [[0,0,0],[0,0,0],[0,0,0]];
    const refW = 1000, refH2 = 1500;
    vaciosObjs.forEach(o => {
      const cx  = (o.bbox.x_min + o.bbox.x_max) / 2;
      const cy  = (o.bbox.y_min + o.bbox.y_max) / 2;
      const col = cx < refW   * 0.33 ? 0 : cx < refW   * 0.66 ? 1 : 2;
      const row = cy < refH2  * 0.33 ? 0 : cy < refH2  * 0.66 ? 1 : 2;
      vaciosGrid[row][col]++;
    });
    const vaciosGridMax = Math.max(...vaciosGrid.flat(), 1);

    return {
      title:       'Análisis de Nevera',
      totalImages: rows.length,
      postobonPanel,
      competenciaPanel,
      vaciosCount:   vaciosObjs.length,
      vaciosGrid,
      vaciosGridMax,
    };
  }

  // ── Helpers de Contaminación ──────────────────────────────────────────────
  private buildContaminationAnalysis(rows: RecognitionRow[]): ContaminationCardConfig {
    const getAttr = (obj: RecognitionObject, type: string): string =>
      obj.attributes?.find(a => a.attribute_type_name === type)?.attribute_name ?? '';

    // Sólo objetos en el espacio Postobón
    const postobonSpace = rows.flatMap(r => r.objects.filter(o => o.tag === 'postobon'));

    const isOwn = (o: RecognitionObject) =>
      getAttr(o, 'company').toLowerCase().includes('postobon');

    const own     = postobonSpace.filter(isOwn);
    const foreign = postobonSpace.filter(o => !isOwn(o));
    const total   = postobonSpace.length || 1;
    const overallPct = (foreign.length / total) * 100;

    // Ranking de marcas contaminantes
    const brandMap: Record<string, { count: number; company: string }> = {};
    foreign.forEach(o => {
      const brand   = getAttr(o, 'brand')   || 'Sin marca';
      const company = getAttr(o, 'company') || 'Sin empresa';
      if (!brandMap[brand]) brandMap[brand] = { count: 0, company };
      brandMap[brand].count++;
    });
    const contaminantBrands = Object.entries(brandMap)
      .map(([brand, v]) => ({
        brand, ...v,
        pct: (v.count / (foreign.length || 1)) * 100,
      }))
      .sort((a, b) => b.count - a.count);

    // Categorías contaminantes
    const catMap: Record<string, number> = {};
    foreign.forEach(o => {
      const cat = getAttr(o, 'category') || 'Sin categoria';
      catMap[cat] = (catMap[cat] ?? 0) + 1;
    });
    const contaminantCategories = Object.entries(catMap)
      .map(([category, count]) => ({
        category, count,
        pct: (count / (foreign.length || 1)) * 100,
      }))
      .sort((a, b) => b.count - a.count);

    // Tabla por imagen
    const imageRows = rows.map(r => {
      const space   = r.objects.filter(o => o.tag === 'postobon');
      const ownC    = space.filter(isOwn).length;
      const foreignC= space.filter(o => !isOwn(o)).length;
      const tot     = space.length || 1;
      return {
        imageUrl:     r.imageUrl,
        timestamp:    '',
        ownCount:     ownC,
        foreignCount: foreignC,
        pctForeign:   (foreignC / tot) * 100,
      };
    }).filter(r => r.ownCount + r.foreignCount > 0)
      .sort((a, b) => b.pctForeign - a.pctForeign);

    const vaciosTotal = rows.flatMap(r => r.objects.filter(o => o.tag === 'vacios')).length;

    return {
      title:       'Contaminación en Espacio Postobón',
      overallPct,
      ownCount:    own.length,
      foreignCount: foreign.length,
      totalInSpace: postobonSpace.length,
      vaciosTotal,
      contaminantBrands,
      contaminantCategories,
      imageRows,
    };
  }
}
